/**
 * SkillCenterPage — global Skill Center (May 15 launch).
 *
 * Visual + structural twin of ConnectorsPage:
 *   • Same 28px hero header + secondary CTAs
 *   • Same 3-toggle view-mode bar (with our extra tabs added)
 *   • Marketplace tab: category filter pills + category-grouped grid
 *   • Detail view: INLINE (replaces grid), not a popup modal
 *   • Detail view has overview / inputs / guardrails / evals / platform sub-tabs
 *   • Card grid: repeat(auto-fill, minmax(320px, 1fr)), gap 14 (marketplace) / 16 (installed)
 *   • Tier + status badges identical to ConnectorsPage's inline TierBadge / ConnectorStatusBadge
 *
 * Six logical tabs, mirroring the locked Skill Center spec:
 *   Marketplace · Installed Skills · Wizard-Managed · Pending Approval · Drift Watch · Dashboard
 *
 * Architecture:
 *   - Custom hooks own per-tab data + loading state.
 *   - Detail view is a separate top-level component, rendered inline when
 *     `marketplaceDetail` state is set (matches ConnectorsPage's pattern).
 *   - Search is debounced and only active in Marketplace tab.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import {
  Sparkles, ShoppingBag, Layers, Package, Clock, AlertTriangle, BarChart3,
  Search, Download, X, Trash2, RefreshCw, ExternalLink, ShieldCheck,
  Zap, Wand2, FileText, ChevronRight, Lock, Plus,
} from 'lucide-react';
import * as api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
/* G90 — re-skin with v2 components. We keep every v1 hook, handler,
 * and API call intact and only swap the chrome (page shell, tabs,
 * cards, buttons, badges, banners) for the v2 design system. */
import {
  V2PageShell, TabNav, Card as V2Card, Button as V2Button, Pill as V2Pill,
  MetricCard as V2MetricCard, Skeleton as V2Skeleton,
  EmptyState as V2EmptyState, Modal as V2Modal,
} from '../components/v2';

// ─── Shared visual tokens (identical to ConnectorsPage palette) ───────
const cardStyle    = { background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 16 };
const inputStyle   = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const labelStyle   = { display: 'block', fontSize: 12, fontWeight: 600, color: '#52525b', marginBottom: 6 };
const metaLabel    = { fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 };
const btnBase      = { padding: '7px 14px', borderRadius: 6, border: '1px solid #e4e4e7', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#52525b', display: 'inline-flex', alignItems: 'center', gap: 5 };
const btnDark      = { ...btnBase, border: 'none', background: '#52525b', color: '#fff' };
const btnAccent    = { ...btnBase, border: 'none', background: '#0d9488', color: '#fff' };
const btnDanger    = { ...btnBase, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b' };
const btnGhost     = { ...btnBase, border: '1px solid #e4e4e7', background: '#fff', color: '#52525b' };

// ─── Domain maps ───────────────────────────────────────────────────────
const KIND_LABELS = {
  connector_skill:     'Connector Skill',
  connector_operation: 'Connector Op',
  workflow_skill:      'Workflow Skill',
  system_skill:        'System Skill',
  runbook_derived:     'Runbook-Derived',
};

// Per-category accent color — used for the left border stripe on cards
// and the active state of the category filter pill. Mirrors
// ConnectorsPage CONNECTOR_TYPES color assignments.
const CATEGORY_COLORS = {
  communication:        '#d946ef',
  endpoint:             '#ea580c',
  identity:             '#2563eb',
  'incident response':  '#dc2626',
  ticketing:            '#16a34a',
  'threat intelligence':'#7c3aed',
  'data security':      '#0d9488',
  network:              '#0891b2',
  cloud:                '#0284c7',
  llm:                  '#8b5cf6',
  decisioning:          '#52525b',
  composite:            '#1e40af',
  utility:              '#52525b',
  uncategorized:        '#52525b',
};
const categoryColor = (cat) => CATEGORY_COLORS[(cat || 'uncategorized').toLowerCase()] || '#52525b';

const KIND_BG = {
  connector_skill:     { bg: '#e3f4f1', border: '#a5d8d0', text: '#085041' },
  connector_operation: { bg: '#e3f4f1', border: '#a5d8d0', text: '#085041' },
  workflow_skill:      { bg: '#dbeafe', border: '#bfdbfe', text: '#1e40af' },
  system_skill:        { bg: '#f4f4f5', border: '#d6d3d1', text: '#57534e' },
  runbook_derived:     { bg: '#fef3c7', border: '#fde68a', text: '#92400e' },
};

const RISK_COLORS = {
  low:      { bg: '#dcfce7', text: '#166534' },
  medium:   { bg: '#f4f4f5', text: '#52525b' },
  high:     { bg: '#fef3c7', text: '#92400e' },
  critical: { bg: '#fee2e2', text: '#991b1b' },
};

const PHASE_COLORS = {
  build:  { bg: '#f5f5f4', border: '#d6d3d1', text: '#57534e' },
  decide: { bg: '#e3f4f1', border: '#a5d8d0', text: '#085041' },
  act:    { bg: '#e3f4f1', border: '#f5b0a8', text: '#9a2f23' },
};

// View-mode tabs — order matches the spec (Marketplace default).
const TABS = [
  { id: 'marketplace', label: 'Marketplace',      icon: ShoppingBag },
  { id: 'installed',   label: 'Installed',        icon: Layers },
  { id: 'wizard',      label: 'Wizard-Managed',   icon: Sparkles },
  { id: 'pending',     label: 'Pending Approval', icon: Clock },
  { id: 'drift',       label: 'Drift Watch',      icon: AlertTriangle },
  { id: 'dashboard',   label: 'Dashboard',        icon: BarChart3 },
];

// ═══════════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════════

export default function SkillCenterPage() {
  const [viewMode, setViewMode]       = useState('marketplace');
  const [marketplaceDetail, setDetail]= useState(null);   // inline detail entry
  const [error, setError]             = useState(null);
  const [info, setInfo]               = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Add Skill router modal — explains the platform model + sends the user
  // to the right home for the kind of skill they want to publish.
  const [showAddSkill, setShowAddSkill] = useState(false);
  // Configure modal — opened from Installed cards. Resolves parent-
  // connector prerequisites before showing tenant-specific config knobs.
  const [configureSkill, setConfigureSkill] = useState(null);

  const marketplace = useMarketplace();
  const installed   = useInstalledSkills();
  const wizard      = useManagedSkills('AUTO_FRAMEWORK_BINDING');
  const pending     = useManagedSkills('AUTO_RUNBOOK_BINDING');

  const loadMarketplace = marketplace.load;
  const loadInstalled   = installed.load;
  const loadWizard      = wizard.load;
  const loadPending     = pending.load;

  useEffect(() => {
    loadMarketplace();
    loadInstalled();
    loadWizard();
    loadPending();
  }, [loadMarketplace, loadInstalled, loadWizard, loadPending]);

  useEffect(() => {
    if (viewMode === 'marketplace') loadMarketplace();
    if (viewMode === 'installed')   loadInstalled();
    if (viewMode === 'wizard')      loadWizard();
    if (viewMode === 'pending')     loadPending();
  }, [viewMode, loadMarketplace, loadInstalled, loadWizard, loadPending]);

  const onInstall = useCallback(async (skill) => {
    setError(null); setInfo(null);
    try {
      const res = await api.installSkill(skill.id);
      if (!res.ok) { setError(res.error || `Failed to install ${skill.displayName}`); return; }
      setInfo(`Installed “${skill.displayName}”.`);
      loadMarketplace();
      loadInstalled();
    } catch (e) { setError(e.message); }
  }, [loadMarketplace, loadInstalled]);

  const onDisable = useCallback(async (skill) => {
    setError(null); setInfo(null);
    try {
      const res = await api.disableSkill(skill.id);
      if (!res.ok) { setError(res.error || `Failed to disable ${skill.displayName}`); return; }
      setInfo(`Disabled “${skill.displayName}”.`);
      loadMarketplace();
      loadInstalled();
    } catch (e) { setError(e.message); }
  }, [loadMarketplace, loadInstalled]);

  // Debounced search (Marketplace only).
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching]   = useState(false);
  useEffect(() => {
    if (viewMode !== 'marketplace' || !searchQuery.trim()) { setSearchHits(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.searchSkillRegistry({ query: searchQuery.trim(), k: 60 });
        if (!cancelled) setSearchHits(res.ok ? (res.data?.items || []) : []);
      } catch (e) {
        if (!cancelled) { setError(e.message); setSearchHits([]); }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQuery, viewMode]);

  /* G90 — v2 TabNav expects { id, label, badge? }. Compute counts
   * inline so the tab badge stays live as the hooks reload. */
  const v2Tabs = TABS.map(t => {
    const count =
      t.id === 'installed' ? installed.totalElements :
      t.id === 'wizard'    ? wizard.items.length :
      t.id === 'pending'   ? pending.items.length :
      null;
    return {
      id: t.id,
      label: t.label,
      badge: count != null && count > 0 ? count : undefined,
    };
  });

  return (
    <V2PageShell
      title="Skill Center"
      subtitle="Browse the platform-wide skill catalog, install skills for your tenant, inspect wizard-owned policy skills, and review runbook-derived skills awaiting Decide-phase sign-off."
      tags={<V2Pill tone="cyan" size="sm">Build</V2Pill>}
      actions={
        <>
          <V2Button
            variant="ghost"
            size="sm"
            iconLeft={<RefreshCw size={13} />}
            onClick={() => { loadMarketplace(); loadInstalled(); loadWizard(); loadPending(); }}
          >
            Refresh
          </V2Button>
          <V2Button
            variant="primary"
            size="sm"
            iconLeft={<Plus size={14} />}
            onClick={() => setShowAddSkill(true)}
          >
            Add Skill
          </V2Button>
        </>
      }
    >
      {/* ── View-mode tab bar ────────────────────────────────────── */}
      <TabNav
        tabs={v2Tabs}
        activeId={viewMode}
        onChange={(id) => { setViewMode(id); setDetail(null); setError(null); setInfo(null); }}
        ariaLabel="Skill Center sections"
      />

      {/* ── Banners ──────────────────────────────────────────────── */}
      <div className="space-y-2 my-4">
        {error && (
          <div className="px-3 py-2 rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger text-[12.5px] flex items-start justify-between gap-3">
            <span><strong>Error:</strong> {error}</span>
            <button onClick={() => setError(null)} className="text-v2-danger hover:opacity-70" aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}
        {info && (
          <div className="px-3 py-2 rounded-v2-sm border border-v2-success bg-v2-success-soft text-v2-success text-[12.5px] flex items-start justify-between gap-3">
            <span>{info}</span>
            <button onClick={() => setInfo(null)} className="text-v2-success hover:opacity-70" aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}
      </div>

      {/* ── Tab body ─────────────────────────────────────────────── */}
      {viewMode === 'marketplace' && (
        marketplaceDetail ? (
          <MarketplaceDetail
            skill={marketplaceDetail}
            onClose={() => setDetail(null)}
            onInstall={onInstall}
            onDisable={onDisable}
          />
        ) : (
          <MarketplaceView
            state={marketplace}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchHits={searchHits}
            searching={searching}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            onOpenDetail={setDetail}
            onInstall={onInstall}
            onDisable={onDisable}
          />
        )
      )}

      {viewMode === 'installed' && (
        <InstalledView
          state={installed}
          onOpenDetail={setDetail}
          onConfigure={setConfigureSkill}
          onDisable={onDisable}
        />
      )}

      {viewMode === 'wizard' && (
        <WizardManagedView state={wizard} onOpenDetail={setDetail} />
      )}

      {viewMode === 'pending' && (
        <PendingApprovalView state={pending} onOpenDetail={setDetail} />
      )}

      {viewMode === 'drift' && <DriftWatchView />}

      {viewMode === 'dashboard' && (
        <DashboardView marketplace={marketplace} installed={installed} wizard={wizard} pending={pending} />
      )}

      {/* ── Add Skill router modal ─────────────────────────────────── */}
      {showAddSkill && (
        <AddSkillModal onClose={() => setShowAddSkill(false)} />
      )}

      {/* ── Configure modal — prerequisite-aware tenant config ─────── */}
      {configureSkill && (
        <ConfigureSkillModal
          skill={configureSkill}
          onClose={() => setConfigureSkill(null)}
          onDisable={(s) => { onDisable(s); setConfigureSkill(null); }}
        />
      )}
    </V2PageShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Marketplace — category pills + grouped grid
// ═══════════════════════════════════════════════════════════════════════

function MarketplaceView({
  state, searchQuery, onSearchChange,
  searchHits, searching,
  categoryFilter, onCategoryFilterChange,
  onOpenDetail, onInstall, onDisable,
}) {
  // Bring the installed flag onto search hits (the search endpoint
  // doesn't include it; the marketplace endpoint does).
  const hits = useMemo(
    () => searchHits ? searchHits.map(e => ({ ...e, installed: state.installedIds.has(e.id) })) : null,
    [searchHits, state.installedIds]
  );

  // Group by category (from the full list, not search hits).
  const grouped = useMemo(() => {
    const buckets = {};
    state.items.forEach(s => {
      const c = (s.category || 'uncategorized').toLowerCase();
      if (!buckets[c]) buckets[c] = [];
      buckets[c].push(s);
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([cat, list]) => ({ cat, list }));
  }, [state.items]);

  const visibleCategories = categoryFilter === 'all'
    ? grouped
    : grouped.filter(g => g.cat === categoryFilter);

  if (state.loading && !state.items.length) return <SkeletonGrid />;
  if (state.error) return <ErrorState onRetry={state.load} message={state.error} />;
  if (!state.items.length) {
    return <EmptyState
      title="No skills in the catalog yet"
      hint="The Skill Registry projects entries from connector manifests at boot. If empty, the bootstrap may still be in progress — check ops logs for [SkillRegistry] ready."
    />;
  }

  return (
    <div>
      {/* Search bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#a1a1aa' }} />
          <input
            type="search"
            placeholder="Search skills (semantic + keyword)…"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 36 }}
          />
          {searching && (
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#a1a1aa' }}>
              searching…
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: '#71717a', whiteSpace: 'nowrap' }}>
          {hits ? `${hits.length} match${hits.length === 1 ? '' : 'es'}` : `${state.items.length} skills`}
        </span>
      </div>

      {/* Category pills (hidden during search) */}
      {!hits && grouped.length > 1 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 22, flexWrap: 'wrap' }}>
          <CategoryPill
            label={`All (${state.items.length})`}
            active={categoryFilter === 'all'}
            onClick={() => onCategoryFilterChange('all')}
          />
          {grouped.map(({ cat, list }) => (
            <CategoryPill
              key={cat}
              label={`${formatCat(cat)} (${list.length})`}
              color={categoryColor(cat)}
              active={categoryFilter === cat}
              onClick={() => onCategoryFilterChange(cat)}
            />
          ))}
        </div>
      )}

      {/* Search results — flat list, no category grouping */}
      {hits && (
        <div style={cardGridStyle(14)}>
          {hits.length === 0 && (
            <EmptyState
              title={`No skills match “${searchQuery}”`}
              hint="Try a different search term, or clear the search to browse the catalog."
            />
          )}
          {hits.map(skill => (
            <MarketplaceCard
              key={skill.id} skill={skill}
              onOpenDetail={onOpenDetail}
              onInstall={onInstall}
              onDisable={onDisable}
            />
          ))}
        </div>
      )}

      {/* Browse — category sections */}
      {!hits && visibleCategories.map(({ cat, list }) => (
        <div key={cat} style={{ marginBottom: 28 }}>
          <CategoryHeader cat={cat} count={list.length} />
          <div style={cardGridStyle(14)}>
            {list.map(skill => (
              <MarketplaceCard
                key={skill.id} skill={skill}
                onOpenDetail={onOpenDetail}
                onInstall={onInstall}
                onDisable={onDisable}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryPill({ label, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px', borderRadius: 99, fontSize: 11, fontWeight: 600,
        border: active ? 'none' : '1px solid #e4e4e7',
        background: active ? (color || '#52525b') : '#fff',
        color: active ? '#fff' : '#52525b',
        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
      {label}
    </button>
  );
}

function CategoryHeader({ cat, count }) {
  const color = categoryColor(cat);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: 6, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Package size={14} style={{ color }} />
      </div>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: 0 }}>{formatCat(cat)}</h3>
      <span style={{ fontSize: 11, color: '#a1a1aa' }}>{count}</span>
    </div>
  );
}

function MarketplaceCard({ skill, onOpenDetail, onInstall, onDisable }) {
  const cat   = (skill.category || 'uncategorized').toLowerCase();
  const color = categoryColor(cat);
  const installed = !!skill.installed;
  const tagsArr = jsonArray(skill.tags);
  const guardArr = jsonArray(skill.guardrails);
  const evalArr  = jsonArray(skill.evals);

  return (
    <div
      onClick={() => onOpenDetail(skill)}
      style={{
        ...cardStyle, cursor: 'pointer',
        borderColor: installed ? '#bbf7d0' : '#e4e4e7',
        borderLeft: `3px solid ${color}`,
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: '#f4f4f5', border: '1px solid #e4e4e7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <KindIcon kind={skill.kind} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h4 style={{
              fontSize: 13, fontWeight: 600, color: '#18181b', margin: 0,
              fontFamily: 'IBM Plex Mono, monospace',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }} title={skill.id}>
              {shortId(skill.id)}
            </h4>
            <div style={{ fontSize: 10, color: '#71717a', marginTop: 1 }}>
              {skill.displayName}{skill.version ? ` · v${skill.version}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <TierBadge tier={skill.tier} />
          {installed && <InstalledPill />}
        </div>
      </div>

      <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 10px 0', lineHeight: 1.4 }}>
        {truncate(skill.description, 120) || (<i>No description.</i>)}
      </p>

      <div style={{ display: 'flex', gap: 10, fontSize: 10, color: '#a1a1aa', marginBottom: 8 }}>
        <span>{guardArr.length} guardrails</span>
        <span>{evalArr.length} evals</span>
        <span>{skill.riskLevel || 'medium'} risk</span>
        {skill.connectorId && <span>via {skill.connectorId}</span>}
      </div>

      {tagsArr.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
          {tagsArr.slice(0, 4).map(t => (
            <span key={t} style={{
              padding: '2px 6px', borderRadius: 99, fontSize: 9,
              background: '#f4f4f5', color: '#71717a', border: '1px solid #e4e4e7',
            }}>{t}</span>
          ))}
        </div>
      )}

      <div style={{
        display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid #f4f4f5',
        alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 10, color: '#a1a1aa' }}>
          {KIND_LABELS[skill.kind] || skill.kind}
        </span>
        {!installed ? (
          <button
            style={btnAccent}
            onClick={(e) => { e.stopPropagation(); onInstall(skill); }}>
            <Download size={12} /> Install Skill
          </button>
        ) : (
          <button
            style={btnGhost}
            onClick={(e) => { e.stopPropagation(); onDisable(skill); }}>
            <Trash2 size={12} /> Disable
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Marketplace Detail (inline — replaces grid)
// ═══════════════════════════════════════════════════════════════════════

function MarketplaceDetail({ skill, onClose, onInstall, onDisable }) {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const isWizardManaged = skill.skillKind === 'AUTO_FRAMEWORK_BINDING';
  const isInstalled = !!skill.installed;
  const cat = (skill.category || 'uncategorized').toLowerCase();
  const color = categoryColor(cat);
  const tagsArr = jsonArray(skill.tags);
  const guardArr = jsonArray(skill.guardrails);
  const evalArr  = jsonArray(skill.evals);
  const canonArr = jsonArray(skill.canonicalNodeTypes);

  const subTabs = [
    { id: 'overview',   label: 'Overview' },
    { id: 'inputs',     label: 'Inputs' },
    { id: 'guardrails', label: `Guardrails (${guardArr.length})` },
    { id: 'evals',      label: `Evals (${evalArr.length})` },
    { id: 'platform',   label: 'Platform Bindings' },
    { id: 'advanced',   label: 'Advanced' },
  ];

  return (
    <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
      {/* Hero header */}
      <div style={{
        padding: 24, borderBottom: '1px solid #e4e4e7',
        background: '#fafaf9',
        display: 'flex', alignItems: 'flex-start', gap: 16,
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: 8,
          background: `${color}15`, border: `1px solid ${color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <KindIcon kind={skill.kind} size={22} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#18181b', margin: 0 }}>
              {skill.displayName || skill.id}
            </h2>
            <TierBadge tier={skill.tier} />
            {isInstalled && <InstalledPill />}
            {isWizardManaged && (
              <span style={{ ...subtleBadgeStyle, background: '#e3f4f1', color: '#085041', borderColor: '#a5d8d0' }}>
                <Lock size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} /> wizard-owned
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#71717a', fontFamily: 'IBM Plex Mono, monospace', marginBottom: 6 }}>
            {skill.id}
          </div>
          <div style={{ fontSize: 12, color: '#71717a' }}>
            {KIND_LABELS[skill.kind] || skill.kind}
            {skill.connectorId ? ` · ${skill.connectorId}` : ''}
            {skill.version    ? ` · v${skill.version}` : ''}
            {skill.category   ? ` · ${formatCat(skill.category)}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {isWizardManaged ? (
            <a href="/policy-config" style={{ ...btnAccent, textDecoration: 'none' }}>
              <Wand2 size={12} /> Open Wizard
            </a>
          ) : isInstalled ? (
            <button style={btnDanger} onClick={() => { onDisable(skill); onClose(); }}>
              <Trash2 size={12} /> Disable
            </button>
          ) : skill.id ? (
            <button style={btnAccent} onClick={() => { onInstall(skill); onClose(); }}>
              <Download size={12} /> Install Skill
            </button>
          ) : null}
          <button style={btnGhost} onClick={onClose}>
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{
        display: 'flex', gap: 0,
        padding: '0 24px',
        borderBottom: '1px solid #e4e4e7',
        background: '#fff',
      }}>
        {subTabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveSubTab(t.id)}
            style={{
              padding: '12px 14px', background: 'transparent', border: 'none',
              fontSize: 12, fontWeight: 600,
              color: activeSubTab === t.id ? '#18181b' : '#71717a',
              borderBottom: activeSubTab === t.id ? '2px solid #18181b' : '2px solid transparent',
              cursor: 'pointer', marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Sub-tab body */}
      <div style={{ padding: 24, minHeight: 280 }}>
        {activeSubTab === 'overview' && (
          <OverviewPanel skill={skill} canonArr={canonArr} tagsArr={tagsArr} />
        )}
        {activeSubTab === 'inputs' && (
          <JsonOrEmpty value={skill.inputs} emptyText="No declared inputs." />
        )}
        {activeSubTab === 'guardrails' && (
          <ChipList values={guardArr} variant="ok"
            emptyText="No guardrails declared by the manifest." />
        )}
        {activeSubTab === 'evals' && (
          <ChipList values={evalArr} variant="info"
            emptyText="No eval suite declared by the manifest." />
        )}
        {activeSubTab === 'platform' && (
          <JsonOrEmpty value={skill.platformBindings}
            emptyText="No platform bindings declared." />
        )}
        {activeSubTab === 'advanced' && <AdvancedPanel skill={skill} />}
      </div>
    </div>
  );
}

function OverviewPanel({ skill, canonArr, tagsArr }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={metaLabel}>Description</div>
        <p style={{ fontSize: 13, color: '#52525b', margin: '4px 0 0 0', lineHeight: 1.55 }}>
          {skill.description || <i>No description provided by the manifest.</i>}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <KeyValuePanel title="Identity" rows={[
          ['kind',         skill.kind],
          ['skillKind',    skill.skillKind],
          ['connector',    skill.connectorId],
          ['operationId',  skill.operationId],
          ['method',       skill.method],
          ['endpoint',     skill.endpoint],
          ['version',      skill.version],
        ]} />
        <KeyValuePanel title="Posture" rows={[
          ['autonomyDefault', skill.autonomyDefault],
          ['riskLevel',       skill.riskLevel],
          ['phase',           skill.phase],
          ['evalScore',       typeof skill.evalScore === 'number' ? skill.evalScore.toFixed(2) : null],
          ['publishedToMcp',  skill.publishedToMcp ? '✓ yes' : 'no'],
          ['credentialType',  skill.credentialType],
          ['tier',            skill.tier],
        ]} />
      </div>

      {canonArr.length > 0 && (
        <div>
          <div style={metaLabel}>Canonical Node Types</div>
          <ChipList values={canonArr} variant="default" />
        </div>
      )}

      {tagsArr.length > 0 && (
        <div>
          <div style={metaLabel}>Tags</div>
          <ChipList values={tagsArr} variant="muted" />
        </div>
      )}
    </div>
  );
}

function AdvancedPanel({ skill }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={metaLabel}>Sample Payload</div>
        <JsonOrEmpty value={skill.samplePayload} emptyText="No sample payload provided." />
      </div>
      <div>
        <div style={metaLabel}>Raw Entry</div>
        <pre style={preStyle}>{JSON.stringify(skill, null, 2)}</pre>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Installed
// ═══════════════════════════════════════════════════════════════════════

function InstalledView({ state, onOpenDetail, onConfigure, onDisable }) {
  const pg = usePaginationState(12);
  if (state.loading && !state.items.length) return <SkeletonGrid />;
  if (state.error) return <ErrorState onRetry={state.load} message={state.error} />;
  if (!state.items.length) {
    return <EmptyState
      title="No skills installed yet"
      hint="Browse the Marketplace and click Install Skill on any card. Tenant grants flow through Teams → Assignments → Infrastructure for scope (Org / Tenant / Agent) configuration."
    />;
  }
  const page = pg.paginate(state.items);
  return (
    <div>
      <div style={cardGridStyle(16)}>
        {page.map(s => (
          <InstalledCard key={s.id} skill={s} onOpenDetail={onOpenDetail} onConfigure={onConfigure} onDisable={onDisable} />
        ))}
      </div>
      <div style={{ marginTop: 18 }}>
        <Pagination
          totalItems={state.items.length}
          page={pg.page}
          pageSize={pg.pageSize}
          onPageChange={pg.setPage}
          onPageSizeChange={pg.setPageSize}
          noun="skills"
        />
      </div>
    </div>
  );
}

function InstalledCard({ skill, onOpenDetail, onConfigure, onDisable }) {
  const cat = (skill.category || 'uncategorized').toLowerCase();
  const color = categoryColor(cat);
  return (
    <div style={cardStyle}>
      <div style={{ height: 3, borderRadius: 2, background: color, marginBottom: 14 }} />
      <div onClick={() => onOpenDetail(skill)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3 style={{
              fontSize: 13, fontWeight: 600, color: '#18181b', margin: 0,
              fontFamily: 'IBM Plex Mono, monospace',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{shortId(skill.id)}</h3>
            <p style={{ fontSize: 11, color: '#71717a', margin: '2px 0 0 0' }}>{skill.displayName}</p>
          </div>
          <InstalledPill />
        </div>
        {skill.description && (
          <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 12px 0', lineHeight: 1.4 }}>
            {truncate(skill.description, 80)}
          </p>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          <RiskBadge risk={skill.riskLevel} />
          {skill.autonomyDefault && (
            <span style={subtleBadgeStyle}>{skill.autonomyDefault}</span>
          )}
          {skill.connectorId && <span style={subtleBadgeStyle}>{skill.connectorId}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, paddingTop: 12, borderTop: '1px solid #f4f4f5' }}>
        <button
          style={{ ...btnBase, flex: 1, justifyContent: 'center' }}
          onClick={() => onConfigure(skill)}
          title="Configure tenant scope, restrictions, and verify parent connector readiness"
        >
          <ChevronRight size={12} /> Configure
        </button>
        <button style={btnDanger} onClick={() => onDisable(skill)} title="Disable for tenant">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Wizard-Managed
// ═══════════════════════════════════════════════════════════════════════

function WizardManagedView({ state, onOpenDetail }) {
  if (state.loading && !state.items.length) return <SkeletonGrid />;
  if (state.error) return <ErrorState onRetry={state.load} message={state.error} />;

  return (
    <div>
      <div style={{
        background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 8,
        padding: '14px 16px', marginBottom: 18,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <Wand2 size={18} style={{ color: '#085041', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, fontSize: 12, color: '#7a2820', lineHeight: 1.55 }}>
          <strong style={{ color: '#5c1d18' }}>Wizard-managed skills.</strong>{' '}
          These were auto-created by the unified policy assignment wizard from a
          framework binding. Don't hand-edit{' '}
          <code style={{ fontFamily: 'IBM Plex Mono, monospace', background: '#fff', padding: '1px 5px', borderRadius: 3, border: '1px solid #a5d8d0', color: '#5c1d18' }}>policy_config</code>
          {' '}— re-run the wizard to update.
        </div>
        <a href="/policy-config" style={{ ...btnAccent, textDecoration: 'none', flexShrink: 0 }}>
          <Wand2 size={12} /> Open Wizard
        </a>
      </div>

      {!state.items.length ? (
        <EmptyState
          title="No wizard-managed skills yet"
          hint="Skills auto-generated by the Policy Skill wizard appear here as soon as you bind a framework. Open the wizard at /policy-config to bind one."
        />
      ) : (
        <div style={cardGridStyle(16)}>
          {state.items.map(s => <WizardCard key={s.id} skill={s} onOpenDetail={onOpenDetail} />)}
        </div>
      )}
    </div>
  );
}

function WizardCard({ skill, onOpenDetail }) {
  const phase = PHASE_COLORS[(skill.phase || skill.skillPhase || 'build').toLowerCase()] || PHASE_COLORS.build;
  return (
    <div style={cardStyle}>
      <div style={{ height: 3, borderRadius: 2, background: '#085041', marginBottom: 14 }} />
      <div onClick={() => onOpenDetail(skill)} style={{ cursor: 'pointer', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>{skill.name || skill.displayName}</div>
            <div style={{ fontSize: 11, color: '#71717a', marginTop: 2, fontFamily: 'IBM Plex Mono, monospace' }}>
              {skill.skillType || 'policy_skill'} · {skill.skillKind || 'AUTO_FRAMEWORK_BINDING'}
            </div>
          </div>
          <span style={{
            padding: '2px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            background: phase.bg, color: phase.text, border: `1px solid ${phase.border}`,
            whiteSpace: 'nowrap',
          }}>
            {(skill.phase || skill.skillPhase || 'build').toUpperCase()}
          </span>
        </div>
        {skill.description && (
          <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 10px 0', lineHeight: 1.4 }}>
            {truncate(skill.description, 110)}
          </p>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={subtleBadgeStyle}>
            <Lock size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />read-only
          </span>
          {typeof skill.evalScore === 'number' && (
            <span style={{ ...subtleBadgeStyle, background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}>
              evalScore {skill.evalScore.toFixed(2)}
            </span>
          )}
          {skill.frameworkBindingId && <span style={subtleBadgeStyle} title={skill.frameworkBindingId}>⛓ bound</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, paddingTop: 10, borderTop: '1px solid #f4f4f5' }}>
        <button style={{ ...btnBase, flex: 1, justifyContent: 'center' }} onClick={() => onOpenDetail(skill)}>
          <ExternalLink size={12} /> View Details
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Pending Approval (runbook-derived; empty until 1.1)
// ═══════════════════════════════════════════════════════════════════════

function PendingApprovalView({ state, onOpenDetail }) {
  if (state.loading && !state.items.length) return <SkeletonGrid />;
  if (state.error) return <ErrorState onRetry={state.load} message={state.error} />;
  return (
    <div>
      <div style={{
        background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8,
        padding: '14px 16px', marginBottom: 18,
        display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 12, color: '#7c4a03', lineHeight: 1.55,
      }}>
        <Clock size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong style={{ color: '#603803' }}>Runbook-derived skills.</strong>{' '}
          When Auto Create lands (1.1), every runbook ingestion will produce skills
          here that need DECIDE-phase review before they can run in production.
          Same review modal as the rest of the Agentlets agent lifecycle.
        </div>
      </div>
      {!state.items.length ? (
        <EmptyState
          title="No skills pending approval"
          hint="Auto Create from runbook is in 1.1. Until then, skills you create manually live in your agents (DECIDE phase)."
        />
      ) : (
        <div style={cardGridStyle(16)}>
          {state.items.map(s => <WizardCard key={s.id} skill={s} onOpenDetail={onOpenDetail} />)}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Drift Watch (placeholder for v1)
// ═══════════════════════════════════════════════════════════════════════

function DriftWatchView() {
  return (
    <div>
      <div style={{
        background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 8,
        padding: '14px 16px', marginBottom: 18,
        display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 12, color: '#7a2820', lineHeight: 1.55,
      }}>
        <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <strong style={{ color: '#5c1d18' }}>Drift Watch.</strong>{' '}
          Surfaces connector-manifest version bumps and workflow-skill upstream
          changes that have flagged installed skills for re-evaluation.
          Automated drift detection is not yet available — this view currently
          shows only manually-flagged items.
        </div>
      </div>
      <EmptyState
        title="No drift detected"
        hint="ConnectorManifestDriftService scans on every boot and stores deltas here. Once a manifest version bumps with breaking changes, affected skills will appear in this list."
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════════════════════

function DashboardView({ marketplace, installed, wizard, pending }) {
  const cards = [
    { label: 'Total catalog',    value: marketplace.items.length,   icon: ShoppingBag, color: '#52525b' },
    { label: 'Installed',        value: installed.totalElements ?? installed.items.length, icon: Layers, color: '#166534' },
    { label: 'Wizard-managed',   value: wizard.items.length,        icon: Sparkles,    color: '#085041' },
    { label: 'Pending approval', value: pending.items.length,       icon: Clock,       color: '#92400e' },
  ];
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        {cards.map(c => (
          <div key={c.label} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={metaLabel}>{c.label}</span>
              <c.icon size={16} style={{ color: c.color }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#18181b' }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: '0 0 14px 0' }}>By kind</h3>
        <KindBreakdown items={marketplace.items} />
      </div>
    </div>
  );
}

function KindBreakdown({ items }) {
  const counts = items.reduce((acc, e) => {
    const k = e.kind || 'connector_skill';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const total = items.length || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Object.entries(counts).map(([k, n]) => {
        const pct = Math.round((n / total) * 100);
        const c = KIND_BG[k] || KIND_BG.connector_skill;
        return (
          <div key={k}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#52525b', marginBottom: 4 }}>
              <span>{KIND_LABELS[k] || k}</span>
              <span>{n} ({pct}%)</span>
            </div>
            <div style={{ height: 6, background: '#f4f4f5', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: c.text }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Hooks (data fetching)
// ═══════════════════════════════════════════════════════════════════════

function useMarketplace() {
  const [items, setItems] = useState([]);
  const [installedIds, setInstalledIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.listSkillRegistry({ page: 0, size: 500 });
      if (!res.ok) { setError(res.error || 'Failed to load Marketplace'); return; }
      const arr = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(arr);
      setInstalledIds(new Set(arr.filter(e => e.installed).map(e => e.id)));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  return { items, installedIds, loading, error, load };
}

function useInstalledSkills() {
  const [items, setItems] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.listInstalledSkills({ page: 0, size: 200 });
      if (!res.ok) { setError(res.error || 'Failed to load Installed Skills'); return; }
      const arr = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(arr);
      setTotalElements(res.data?.totalElements ?? arr.length);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  return { items, totalElements, loading, error, load };
}

function useManagedSkills(skillKind) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.listManagedSkills({ skillKind });
      if (!res.ok) { setError(res.error || `Failed to load ${skillKind}`); return; }
      const arr = Array.isArray(res.data) ? res.data : [];
      setItems(arr);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [skillKind]);

  return { items, loading, error, load };
}

// ═══════════════════════════════════════════════════════════════════════
// Reusable visual primitives
// ═══════════════════════════════════════════════════════════════════════

const cardGridStyle = (gap = 16) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap,
});

const subtleBadgeStyle = {
  padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500,
  background: '#f4f4f5', color: '#52525b', border: '1px solid #e4e4e7',
  fontFamily: 'IBM Plex Mono, monospace',
  display: 'inline-flex', alignItems: 'center',
};

const preStyle = {
  background: '#1c1917', color: '#e7e5e4', padding: 12, borderRadius: 6,
  fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', overflow: 'auto',
  maxHeight: 320, margin: 0,
};

function TierBadge({ tier }) {
  const t = (tier || 'free').toLowerCase();
  const palette = {
    free:       { bg: '#dcfce7', text: '#166534' },
    premium:    { bg: '#fef3c7', text: '#92400e' },
    enterprise: { bg: '#ede9fe', text: '#5b21b6' },
  }[t] || { bg: '#f4f4f5', text: '#52525b' };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: palette.bg, color: palette.text,
    }}>{t}</span>
  );
}

function InstalledPill() {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0',
      whiteSpace: 'nowrap',
    }}>installed</span>
  );
}

function RiskBadge({ risk }) {
  const k = (risk || 'medium').toLowerCase();
  const c = RISK_COLORS[k] || RISK_COLORS.medium;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 500,
      background: c.bg, color: c.text,
    }}>risk: {k}</span>
  );
}

function KindIcon({ kind, size = 14 }) {
  switch (kind) {
    case 'workflow_skill':    return <Zap size={size} style={{ color: '#1e40af' }} />;
    case 'system_skill':      return <ShieldCheck size={size} style={{ color: '#57534e' }} />;
    case 'runbook_derived':   return <FileText size={size} style={{ color: '#92400e' }} />;
    case 'connector_operation':
    case 'connector_skill':
    default:                  return <Package size={size} style={{ color: '#085041' }} />;
  }
}

function KeyValuePanel({ title, rows }) {
  const visible = rows.filter(([, v]) => v != null && v !== '');
  if (!visible.length) return null;
  return (
    <div style={{ background: '#fafaf9', border: '1px solid #e4e4e7', borderRadius: 6, padding: 12 }}>
      <div style={{ ...labelStyle, marginBottom: 6 }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <tbody>
          {visible.map(([k, v]) => (
            <tr key={k}>
              <td style={{ padding: '3px 0', color: '#71717a', verticalAlign: 'top', width: 130, fontFamily: 'IBM Plex Mono, monospace' }}>{k}</td>
              <td style={{ padding: '3px 0', color: '#18181b', wordBreak: 'break-word' }}>{String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChipList({ values, variant = 'default', emptyText }) {
  const variantStyles = {
    default: { bg: '#f4f4f5', text: '#52525b', border: '#e4e4e7' },
    ok:      { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
    info:    { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
    muted:   { bg: '#fafaf9', text: '#71717a', border: '#e4e4e7' },
  }[variant] || { bg: '#f4f4f5', text: '#52525b', border: '#e4e4e7' };
  if (!values || !values.length) {
    return emptyText
      ? <p style={{ fontSize: 12, color: '#a1a1aa', fontStyle: 'italic', margin: 0 }}>{emptyText}</p>
      : null;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {values.map(v => (
        <span key={v} style={{
          padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
          fontFamily: 'IBM Plex Mono, monospace',
          background: variantStyles.bg, color: variantStyles.text,
          border: `1px solid ${variantStyles.border}`,
        }}>{v}</span>
      ))}
    </div>
  );
}

function JsonOrEmpty({ value, emptyText }) {
  const isEmpty =
    value == null ||
    (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) {
    return <p style={{ fontSize: 12, color: '#a1a1aa', fontStyle: 'italic', margin: 0 }}>{emptyText}</p>;
  }
  return <pre style={preStyle}>{JSON.stringify(value, null, 2)}</pre>;
}

function Banner({ kind, children, onClose }) {
  const styles = kind === 'error'
    ? { bg: '#fee2e2', border: '#fecaca', text: '#991b1b' }
    : { bg: '#dcfce7', border: '#bbf7d0', text: '#166534' };
  return (
    <div style={{
      background: styles.bg, border: `1px solid ${styles.border}`, borderRadius: 6,
      padding: '10px 14px', marginBottom: 16,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      fontSize: 12, color: styles.text,
    }}>
      <span>{kind === 'error' ? '⚠ ' : '✓ '}{children}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: styles.text }}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function EmptyState({ title, hint }) {
  return (
    <div style={{
      ...cardStyle, padding: 48, textAlign: 'center',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    }}>
      <div style={{ fontSize: 28, opacity: 0.5 }}>📋</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#52525b' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#71717a', maxWidth: 540, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div style={{
      ...cardStyle, padding: 32, textAlign: 'center',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      borderColor: '#fecaca', background: '#fef2f2',
    }}>
      <AlertTriangle size={28} style={{ color: '#991b1b' }} />
      <div style={{ fontSize: 13, color: '#991b1b' }}>{message}</div>
      {onRetry && <button style={btnBase} onClick={onRetry}><RefreshCw size={12} /> Retry</button>}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={cardGridStyle(14)}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ ...cardStyle, height: 200, position: 'relative', overflow: 'hidden' }}>
          <div style={{ width: '60%', height: 14, background: '#f4f4f5', borderRadius: 3, marginBottom: 10 }} />
          <div style={{ width: '40%', height: 10, background: '#f4f4f5', borderRadius: 3, marginBottom: 16 }} />
          <div style={{ width: '95%', height: 8, background: '#f4f4f5', borderRadius: 3, marginBottom: 6 }} />
          <div style={{ width: '85%', height: 8, background: '#f4f4f5', borderRadius: 3, marginBottom: 6 }} />
          <div style={{ width: '70%', height: 8, background: '#f4f4f5', borderRadius: 3 }} />
        </div>
      ))}
    </div>
  );
}

// ─── Pure helpers ──────────────────────────────────────────────────────

function jsonArray(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') return Object.values(v);
  return [];
}

function shortId(id) {
  if (!id) return '';
  for (const prefix of ['connector:', 'workflow:', 'system:', 'runbook:']) {
    if (id.startsWith(prefix)) return id.substring(prefix.length);
  }
  return id;
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.substring(0, n) + '…' : s;
}

function formatCat(cat) {
  if (!cat) return 'Uncategorized';
  return cat.split(/[\s_-]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ═══════════════════════════════════════════════════════════════════════
// Add Skill — router modal
// ═══════════════════════════════════════════════════════════════════════
//
// Skills enter the platform through five paths, each with its own home:
//
//   1. Connector manifest projection ......... Platform admin (auto at boot)
//   2. Policy Skill Wizard .................... /policy-config (existing)
//   3. Workflow → workflow_skill .............. NEW — this modal's primary CTA
//   4. Hand-authored skill on agent ........... /agents → Agent → Add Skill (existing)
//   5. Auto Create runbook ingestion .......... /agents → Auto Create (1.1)
//
// This modal is the home for path #3 (the gap before V161) AND a router
// that explains the model and points users at the right home for the
// other paths.

function AddSkillModal({ onClose }) {
  // Two-pane modal:
  //   `view='paths'`     → the original three path-cards
  //   `view='publishWorkflow'` → inline workflow-publisher form
  // Reference: WORKFLOW_SKILL_RELATIONSHIP_DESIGN.md §5 item 1.
  const [view, setView] = useState('paths');

  if (view === 'publishWorkflow') {
    return (
      <ModalShell
        title="Publish a workflow as a skill"
        onClose={onClose}
      >
        <PublishWorkflowForm
          onCancel={() => setView('paths')}
          onSuccess={onClose}
        />
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Add a skill to the catalog" onClose={onClose}>
      <p style={{ fontSize: 13, color: '#52525b', margin: '0 0 18px 0', lineHeight: 1.55 }}>
        The Skill Center is the platform-wide catalog. Skills enter it through
        a few different paths — pick the one that matches what you're trying
        to do.
      </p>

      <PathCard
        icon={<Zap size={18} style={{ color: '#1e40af' }} />}
        title="Publish a Workflow as a Skill"
        description="Promote one of your tenant's active Canvas workflows into the platform Skill Registry so any agent can call it as a single step. Recursive composition — workflow_skill kind."
        cta="Choose a workflow"
        onClick={() => setView('publishWorkflow')}
        primary
      />

      <PathCard
        icon={<Plus size={18} style={{ color: '#52525b' }} />}
        title="Add a custom skill to a specific agent"
        description="Hand-author a Standard / Workflow / Connector / Policy skill scoped to one agent. Use this for one-off bespoke skills that don't need to be reused. Permissions: skill.create + agent.edit."
        cta="Open Agent Lifecycle"
        onClick={() => { window.location.href = '/agents'; }}
      />

      <PathCard
        icon={<Wand2 size={18} style={{ color: '#085041' }} />}
        title="Bind a compliance framework"
        description="Use the unified Policy Skill Wizard to bind a regulatory framework (SOC 2, NIST, HIPAA, …) and auto-generate the policy skills that enforce its controls. Wizard-managed; appears in the Wizard-Managed tab."
        cta="Open Policy Skill Wizard"
        onClick={() => { window.location.href = '/policy-config'; }}
      />

      <div style={{
        marginTop: 18, padding: '10px 14px',
        background: '#fafaf9', border: '1px solid #e4e4e7', borderRadius: 6,
        fontSize: 11, color: '#71717a', lineHeight: 1.55,
      }}>
        <strong style={{ color: '#52525b' }}>Coming in 1.1:</strong>{' '}
        Auto Create from Runbook will produce skills here too — they'll
        appear in the Pending Approval tab awaiting DECIDE-phase review.
      </div>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PublishWorkflowForm — inline workflow picker + publish action
// ═══════════════════════════════════════════════════════════════════════
//
// Replaces the old "Choose a Workflow → /workflows page" router-redirect
// with a self-contained flow:
//
//   1. Load the tenant's workflows; show only status='active' (publish gate).
//   2. User picks one, optionally overrides displayName / description.
//   3. Optionally selects an agent for AUTO_REGISTRY_INSTALL — the install
//      bridge materialises a Skill row on that agent so the new
//      workflow_skill is invocable from its canvas immediately.
//   4. [Publish to Skill Registry] → publishWorkflowAsSkill, then if an
//      agent was chosen, installSkill(registryId, { agentId }).
//
// Reference: WORKFLOW_SKILL_RELATIONSHIP_DESIGN.md §5 items 1 + 2.
//
function PublishWorkflowForm({ onCancel, onSuccess }) {
  const { session } = useAuth();

  const [workflows, setWorkflows]               = useState([]);
  const [allWorkflowCount, setAllWorkflowCount] = useState(0);
  const [agents, setAgents]                     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [loadError, setLoadError]               = useState(null);

  const [selectedWfId, setSelectedWfId] = useState('');
  const [displayName, setDisplayName]   = useState('');
  const [description, setDescription]   = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);

      // The Workflows controller requires `orgId` OR `tenantId` (or
      // `agentId`) in the query string — passing none returns 400. We
      // pull them off the auth session so the picker works regardless
      // of role. Empty/null values are dropped by the api client.
      const scope = {};
      if (session?.orgId)    scope.orgId    = session.orgId;
      if (session?.tenantId) scope.tenantId = session.tenantId;

      try {
        const [wfRes, agRes] = await Promise.all([
          api.getWorkflows(scope),
          api.getAgents(scope),
        ]);
        if (cancelled) return;

        // apiFetch wraps every response as { ok, data, ... }. Unwrap
        // before treating as a list. Both endpoints return raw arrays
        // on success, so .data is the array we want.
        const wfList = wfRes?.ok && Array.isArray(wfRes.data) ? wfRes.data
                      : (Array.isArray(wfRes) ? wfRes : []);
        const agList = agRes?.ok && Array.isArray(agRes.data) ? agRes.data
                      : (Array.isArray(agRes) ? agRes : []);

        // Status gate: 'active' is the canonical publishable state per
        // the design doc. Drafts and archived workflows aren't eligible
        // — but we want to tell the user *why* their list is empty if
        // they have non-active workflows, so we keep the unfiltered
        // count separately.
        setAllWorkflowCount(wfList.length);
        setWorkflows(wfList.filter(w => (w.status || '').toLowerCase() === 'active'));
        setAgents(agList);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Failed to load workflows / agents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session?.orgId, session?.tenantId]);

  const selectedWorkflow = useMemo(
    () => workflows.find(w => w.id === selectedWfId),
    [workflows, selectedWfId]
  );

  const canSubmit = !!selectedWfId && !submitting;

  const handlePublish = async () => {
    if (!selectedWfId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Step 1 — promote workflow to Skill Registry.
      // apiFetch wraps every successful response as { ok, data, ... }.
      // The publish endpoint returns { registryId, kind, displayName,
      // version, workflowId } in `data`.
      const publishRes = await api.publishWorkflowAsSkill({
        workflowId: selectedWfId,
        displayName: displayName || undefined,
        description: description || undefined,
      });
      const payload = publishRes?.ok ? publishRes.data : publishRes;
      const registryId = payload?.registryId;
      if (!registryId) {
        throw new Error('Publish succeeded but server returned no registryId.');
      }
      // Step 2 — optional agent install via the bridge. Same envelope
      // shape; we don't need to read its body, just want it to succeed.
      if (selectedAgentId) {
        const installRes = await api.installSkill(registryId, { agentId: selectedAgentId });
        if (installRes && installRes.ok === false) {
          // apiFetch returns ok:false for 409 conflicts (rare here —
          // re-installing on an agent is idempotent server-side, but be
          // explicit). Treat as soft success and surface a warning.
          // eslint-disable-next-line no-console
          console.warn('[Publish] install bridge returned non-ok', installRes);
        }
      }
      onSuccess?.();
    } catch (e) {
      // Backend maps draft-workflow → 409 with code=workflow_not_publishable;
      // unknown workflow → 400. Surface the message the API returned.
      setSubmitError(e?.message || 'Publish failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px 8px', textAlign: 'center', color: '#71717a', fontSize: 13 }}>
        Loading workflows…
      </div>
    );
  }
  if (loadError) {
    return (
      <div style={{
        background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 6,
        padding: 14, fontSize: 12, color: '#7a2820',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <AlertTriangle size={14} style={{ color: '#085041' }} />
          <strong style={{ color: '#5c1d18' }}>Couldn't load picker data</strong>
        </div>
        {loadError}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: '#52525b', margin: 0, lineHeight: 1.55 }}>
        Promote one of your active workflows to the platform Skill Registry.
        Once published, the workflow becomes a callable <em>workflow_skill</em>
        — drop it onto any agent's canvas as a single <code>skill_invoke</code>
        node.
      </p>

      {/* Workflow picker */}
      <div>
        <label style={labelStyle}>Workflow <span style={{ color: '#085041' }}>*</span></label>
        {workflows.length === 0 ? (
          <div style={{
            background: '#fafaf9', border: '1px solid #e4e4e7', borderRadius: 6,
            padding: 12, fontSize: 12, color: '#71717a', lineHeight: 1.5,
          }}>
            {allWorkflowCount === 0 ? (
              <>
                No workflows found in this org/tenant. Build one in the
                <a href="/workflows" style={{ color: '#0d9488', textDecoration: 'none', fontWeight: 600 }}>
                  {' '}Workflow Canvas
                </a>{' '}first, then come back to publish it.
              </>
            ) : (
              <>
                You have <strong>{allWorkflowCount}</strong> workflow
                {allWorkflowCount === 1 ? '' : 's'} but none are in
                <code style={{ fontSize: 11 }}> status=active</code>. Only active
                workflows can be published — drafts, paused, and archived
                workflows aren't eligible.{' '}
                <a href="/workflows" style={{ color: '#0d9488', textDecoration: 'none', fontWeight: 600 }}>
                  Activate one
                </a>{' '}and reopen this dialog.
              </>
            )}
          </div>
        ) : (
          <select
            value={selectedWfId}
            onChange={(e) => setSelectedWfId(e.target.value)}
            style={{ ...inputStyle, padding: '8px 10px' }}
          >
            <option value="">Select a workflow…</option>
            {workflows.map(w => (
              <option key={w.id} value={w.id}>
                {w.name}{w.versionLabel ? ` · ${w.versionLabel}` : (w.version ? ` · v${w.version}` : '')}
              </option>
            ))}
          </select>
        )}
        {selectedWorkflow?.description && (
          <p style={{ fontSize: 11, color: '#71717a', margin: '6px 2px 0 2px', lineHeight: 1.45 }}>
            {selectedWorkflow.description}
          </p>
        )}
      </div>

      {/* Display name override */}
      <div>
        <label style={labelStyle}>Display name <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional — defaults to workflow name)</span></label>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={selectedWorkflow?.name || 'How this skill appears in the marketplace'}
          style={inputStyle}
        />
      </div>

      {/* Description */}
      <div>
        <label style={labelStyle}>Description <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional)</span></label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="One or two lines explaining what this workflow does as a skill."
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      {/* Agent install bridge */}
      <div>
        <label style={labelStyle}>Install on an agent now <span style={{ color: '#a1a1aa', fontWeight: 400 }}>(optional)</span></label>
        <select
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
          style={{ ...inputStyle, padding: '8px 10px' }}
          disabled={agents.length === 0}
        >
          <option value="">Don't auto-install — just publish to the registry</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>
              {a.name || a.displayName || a.id}
            </option>
          ))}
        </select>
        <p style={{ fontSize: 11, color: '#71717a', margin: '6px 2px 0 2px', lineHeight: 1.45 }}>
          {agents.length === 0 ? (
            <>
              No agents found in this org/tenant. The workflow will still
              be published — install it on an agent later from the Skill
              Center → Marketplace tab.
            </>
          ) : (
            <>
              Picking an agent additionally creates an{' '}
              <code style={{ fontSize: 10 }}>AUTO_REGISTRY_INSTALL</code> Skill row
              on that agent so this workflow_skill is callable from its canvas
              immediately. The skill enters the agent's <em>Build</em> phase
              for review.
            </>
          )}
        </p>
      </div>

      {/* Submit error */}
      {submitError && (
        <div style={{
          background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 6,
          padding: 12, fontSize: 12, color: '#7a2820',
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={14} style={{ color: '#085041', flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong style={{ color: '#5c1d18' }}>Publish failed</strong>
            <div style={{ marginTop: 4 }}>{submitError}</div>
          </div>
        </div>
      )}

      {/* Action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button type="button" style={btnGhost} onClick={onCancel} disabled={submitting}>
          Back
        </button>
        <button
          type="button"
          style={{
            ...btnAccent,
            opacity: canSubmit ? 1 : 0.5,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
          disabled={!canSubmit}
          onClick={handlePublish}
        >
          {submitting ? 'Publishing…' : 'Publish to Skill Registry'}
        </button>
      </div>
    </div>
  );
}

function PathCard({ icon, title, description, cta, onClick, primary }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff',
        border: primary ? '1px solid #a5d8d0' : '1px solid #e4e4e7',
        borderLeft: primary ? '3px solid #0d9488' : '3px solid #e4e4e7',
        borderRadius: 8, padding: '14px 16px', marginBottom: 10,
        cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
        display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8,
        background: primary ? '#e3f4f1' : '#f4f4f5',
        border: primary ? '1px solid #a5d8d0' : '1px solid #e4e4e7',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#18181b', margin: 0 }}>{title}</h4>
        </div>
        <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 8px 0', lineHeight: 1.5 }}>
          {description}
        </p>
        <div style={{ fontSize: 11, fontWeight: 600, color: primary ? '#0d9488' : '#52525b', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {cta} <ChevronRight size={11} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Configure — prerequisite-aware tenant config modal
// ═══════════════════════════════════════════════════════════════════════
//
// Opens when a user clicks Configure on an Installed Skill card.
// Comprehensive flow:
//
//   1. Resolve parent (connector_skill → connector; workflow_skill →
//      workflow; system_skill → none; runbook_derived → runbook).
//   2. Check parent status — for connector_skill the parent must be
//      installed AND active. If not, render a "Resolve prerequisite"
//      view with a CTA to go fix the parent.
//   3. If parent is ready (or no parent), render the configuration view:
//      tenant grant info, restrictions placeholder, Disable action.
//
// V1 surfaces the prerequisite check + grant info + Disable. The
// per-skill restrictions JSON editor lands in 1.x — the JSONB column
// (skill_registry_tenant_grant.restrictions) is already there in V161,
// so the API is ready when the UI is.

function ConfigureSkillModal({ skill, onClose, onDisable }) {
  const [parentStatus, setParentStatus] = useState({ phase: 'checking' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = await resolveParentStatus(skill);
      if (!cancelled) setParentStatus(status);
    })();
    return () => { cancelled = true; };
  }, [skill]);

  const ready = parentStatus.phase === 'ready';
  const blocked = parentStatus.phase === 'blocked';
  const noParent = parentStatus.phase === 'no_parent';
  const checking = parentStatus.phase === 'checking';

  return (
    <ModalShell
      title={(
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>Configure</span>
          <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#71717a' }}>
            {shortId(skill.id)}
          </span>
        </span>
      )}
      onClose={onClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Skill identity strip */}
        <div style={{
          background: '#fafaf9', border: '1px solid #e4e4e7',
          borderRadius: 6, padding: 12,
          display: 'flex', gap: 10, alignItems: 'center',
        }}>
          <KindIcon kind={skill.kind} size={18} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#18181b' }}>
              {skill.displayName || skill.id}
            </div>
            <div style={{ fontSize: 11, color: '#71717a', marginTop: 2 }}>
              {KIND_LABELS[skill.kind] || skill.kind}
              {skill.connectorId ? ` · ${skill.connectorId}` : ''}
              {skill.version    ? ` · v${skill.version}` : ''}
            </div>
          </div>
          <RiskBadge risk={skill.riskLevel} />
        </div>

        {/* Prerequisite block */}
        <div>
          <div style={metaLabel}>Prerequisites</div>
          {checking && (
            <div style={{
              background: '#fff', border: '1px solid #e4e4e7', borderRadius: 6,
              padding: 12, fontSize: 12, color: '#71717a',
            }}>
              Checking parent connector status…
            </div>
          )}
          {ready && (
            <div style={{
              background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 6,
              padding: 12, fontSize: 12, color: '#166534',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <ShieldCheck size={16} />
              <span><strong>{parentStatus.label}</strong> — {parentStatus.detail}</span>
            </div>
          )}
          {blocked && (
            <div style={{
              background: '#e3f4f1', border: '1px solid #a5d8d0', borderRadius: 6,
              padding: 12, fontSize: 12, color: '#7a2820',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={16} style={{ color: '#085041' }} />
                <strong style={{ color: '#5c1d18' }}>{parentStatus.label}</strong>
              </div>
              <p style={{ margin: '0 0 10px 0', lineHeight: 1.5 }}>
                {parentStatus.detail}
              </p>
              {parentStatus.fixUrl && (
                <a href={parentStatus.fixUrl} style={{ ...btnAccent, textDecoration: 'none' }}>
                  {parentStatus.fixIcon} {parentStatus.fixLabel}
                </a>
              )}
            </div>
          )}
          {noParent && (
            <div style={{
              background: '#f4f4f5', border: '1px solid #e4e4e7', borderRadius: 6,
              padding: 12, fontSize: 12, color: '#52525b',
            }}>
              {parentStatus.detail}
            </div>
          )}
        </div>

        {/* Tenant grant info */}
        <div>
          <div style={metaLabel}>Tenant grant</div>
          <div style={{
            background: '#fafaf9', border: '1px solid #e4e4e7', borderRadius: 6,
            padding: 12, fontSize: 12, color: '#52525b', lineHeight: 1.6,
          }}>
            <div>Status: <strong style={{ color: '#166534' }}>granted, verified active</strong></div>
            <div>Scope: <strong style={{ color: '#18181b' }}>tenant-wide</strong> (override per-Org / per-Agent in <a href="/team" style={{ color: '#0d9488' }}>Teams → Assignments → Infrastructure</a>)</div>
            <div>Autonomy default: <strong style={{ color: '#18181b' }}>{skill.autonomyDefault || 'HUMAN_APPROVED'}</strong></div>
          </div>
        </div>

        {/* Restrictions — placeholder for v1 */}
        <div>
          <div style={metaLabel}>Restrictions</div>
          <div style={{
            background: '#fafaf9', border: '1px dashed #d6d3d1', borderRadius: 6,
            padding: 14, fontSize: 12, color: '#71717a', textAlign: 'center',
          }}>
            <Lock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Per-skill tenant restrictions editor lands in 1.x. The
            <code style={{ fontFamily: 'IBM Plex Mono, monospace', margin: '0 4px' }}>
              skill_registry_tenant_grant.restrictions
            </code>
            JSONB column (V161) is the persisted target.
          </div>
        </div>

        {/* Action row */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          paddingTop: 8, borderTop: '1px solid #f4f4f5',
        }}>
          {ready && (
            <button style={btnDanger} onClick={() => onDisable(skill)}>
              <Trash2 size={12} /> Disable for tenant
            </button>
          )}
          <button style={btnGhost} onClick={onClose}>Close</button>
        </div>
      </div>
    </ModalShell>
  );
}

/**
 * Resolve the prerequisite status for a skill. Returns one of:
 *   { phase: 'checking' }                                 — initial state
 *   { phase: 'ready',     label, detail }                 — parent ready (or no parent)
 *   { phase: 'no_parent', detail }                        — system_skill etc.
 *   { phase: 'blocked',   label, detail, fixUrl, fixLabel, fixIcon }
 */
async function resolveParentStatus(skill) {
  // System skills: always usable — no parent.
  if (skill.kind === 'system_skill') {
    return {
      phase: 'no_parent',
      detail: 'System skills are platform built-ins. Always available; no parent connector required.',
    };
  }
  // Workflow skills: parent is the source workflow. We don't fetch its
  // status here — the link to the workflow surface is enough for v1.
  if (skill.kind === 'workflow_skill') {
    return {
      phase: 'ready',
      label: 'Workflow skill',
      detail: 'Backed by a published Canvas workflow. Visit the Workflows page to inspect its status and revisions.',
    };
  }
  // Runbook-derived (1.1): parent is the runbook + the underlying
  // resolved skills. v1 stub.
  if (skill.kind === 'runbook_derived') {
    return {
      phase: 'ready',
      label: 'Runbook-derived skill',
      detail: 'Composed from a runbook ingestion. The DECIDE-phase review owns its readiness contract.',
    };
  }
  // Connector skill / connector operation — must have parent connector
  // installed AND active for the calling tenant.
  const connectorId = skill.connectorId;
  if (!connectorId) {
    return {
      phase: 'ready',
      label: 'Standalone skill',
      detail: 'No parent connector dependency declared.',
    };
  }
  try {
    const res = await api.getConnectors({});
    const connectors = res.ok && Array.isArray(res.data) ? res.data : [];
    // The connector record's `type` matches our connectorId (e.g. 'slack').
    const matches = connectors.filter(
      c => (c.type || '').toLowerCase() === connectorId.toLowerCase()
    );
    if (matches.length === 0) {
      return {
        phase: 'blocked',
        label: `${connectorId} connector not installed`,
        detail: `This skill calls the ${connectorId} API. Install the ${connectorId} connector and complete its OAuth/credential setup before configuring the skill.`,
        fixUrl: '/connectors',
        fixLabel: `Install ${connectorId} connector`,
        fixIcon: <Plus size={12} />,
      };
    }
    const active = matches.find(
      c => (c.status || '').toLowerCase() === 'connected'
        || (c.status || '').toLowerCase() === 'active'
    );
    if (!active) {
      const sample = matches[0];
      return {
        phase: 'blocked',
        label: `${connectorId} connector not active`,
        detail: `Found ${matches.length} ${connectorId} connector(s) but none are connected. Status: ${sample.status || 'unknown'}. Reconnect the connector before configuring the skill.`,
        fixUrl: '/connectors',
        fixLabel: `Open ${connectorId} connector`,
        fixIcon: <ExternalLink size={12} />,
      };
    }
    return {
      phase: 'ready',
      label: `${connectorId} connector active`,
      detail: `Verified connected (${active.name || active.id}). Skill is ready to be invoked by agents in this tenant.`,
    };
  } catch (e) {
    return {
      phase: 'blocked',
      label: 'Connector status check failed',
      detail: `Could not reach the connectors API: ${e.message}. Try again or check the Connectors page directly.`,
      fixUrl: '/connectors',
      fixLabel: 'Open Connectors',
      fixIcon: <ExternalLink size={12} />,
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Lightweight modal shell — Skill Center scope only.
// We don't reuse <Modal> from common/Modal.jsx because (a) the SkillCenter
// already operates without that import (kept lean by design), and (b) we
// want a subtly tighter visual treatment for the Add Skill / Configure
// modals that's specific to this page.
// ═══════════════════════════════════════════════════════════════════════

function ModalShell({ title, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '64px 16px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, maxWidth: 560, width: '100%',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #e4e4e7',
        }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#18181b', margin: 0 }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#71717a', display: 'flex', alignItems: 'center',
              padding: 4, borderRadius: 4,
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}
